import { useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import type * as React from 'react';

import { cn } from '../../lib/utils';

const DEFAULT_WIDTH = 180;

/**
 * Space left between the bottom of the *card* and the bottom of the window. The
 * list grows to fill whatever is above that and stops, so a long vocabulary is
 * one scroll inside the card rather than a card that runs off the screen.
 */
const VIEWPORT_GUTTER_PX = 100;

/** Below this the list is too short to scroll usefully. */
const MIN_LIST_HEIGHT_PX = 120;

/**
 * The list's height budget: everything from its own top edge to the window
 * bottom, less the gutter. Measured rather than expressed in CSS because the
 * card is anchored to a widget number that can sit anywhere down the page —
 * `calc(100vh - …)` would ignore that offset and overflow for every anchor
 * below the fold.
 *
 * The list's own top does not move when its max-height changes — it is the last
 * row of a card anchored at a fixed top — so this measures once per mount and
 * on resize, and never feeds back into itself.
 */
function useListMaxHeight(itemCount: number): {
  listRef: React.RefObject<HTMLUListElement>;
  maxHeight: number | undefined;
  shiftUp: number;
} {
  const listRef = useRef<HTMLUListElement>(null);
  const [maxHeight, setMaxHeight] = useState<number>();
  const [shiftUp, setShiftUp] = useState(0);
  // Mirrors `shiftUp` for the measure callback, which is registered once and
  // would otherwise close over the mount-time value.
  const shiftRef = useRef(0);

  useLayoutEffect(() => {
    const list = listRef.current;
    if (list === null) return;

    const measure = (): void => {
      const listRect = list.getBoundingClientRect();
      const card = list.parentElement;
      // `getBoundingClientRect` reports the TRANSFORMED box, so on a resize it
      // would report the card where the last shift already put it — the budget
      // would come back larger, the shift would be recomputed as zero, and the
      // card would drop back off the bottom of the screen. Adding the applied
      // shift back gives the untransformed geometry every pass measures from.
      const applied = shiftRef.current;
      const listTop = listRect.top + applied;
      // The card's own padding sits below the list, so the gutter has to be
      // measured from the card's edge or it comes up short by that much. The
      // difference is padding, not content: it does not move when the list
      // shrinks — and being a difference of two boxes, the transform cancels.
      const cardRect = card === null ? listRect : card.getBoundingClientRect();
      const cardTop = cardRect.top + applied;
      const chromeBelow = card === null ? 0 : cardRect.bottom - listRect.bottom;
      const budget = window.innerHeight - listTop - chromeBelow - VIEWPORT_GUTTER_PX;

      // Shrinking alone cannot save a card anchored near the bottom of the
      // page: below a certain point the budget goes to nothing, and clamping to
      // the minimum would push the card off-screen — the exact defect the
      // measuring is here to prevent. So keep the minimum and pull the card up
      // by however much it overshoots, never past the top of the viewport.
      const height = Math.max(MIN_LIST_HEIGHT_PX, budget);
      const overshoot = height - budget;
      const next = Math.min(Math.max(0, overshoot), Math.max(0, cardTop - VIEWPORT_GUTTER_PX));
      shiftRef.current = next;
      setMaxHeight(height);
      setShiftUp(next);
    };

    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [itemCount]);

  return { listRef, maxHeight, shiftUp };
}

export interface WidgetBreakdownItem {
  /** Stable identifier — used as the React key for the row. */
  id: string;
  /** Visible row label (left column). */
  label: string;
  /** Raw count, passed through `formatCount` before display. */
  count: number;
  /**
   * When true, the row is rendered in the "emphasis" red color
   * (`text-error`). Design §6 #4 isolates this decision to a single styled
   * span so swapping the treatment stays a one-line change.
   */
  isHighlighted?: boolean;
  /** Pre-formatted size string (e.g. `'1.5 GB'`). Rendered as the rightmost column in the stale breakdown inline box. */
  sizeLabel?: string;
}

export interface WidgetBreakdownPopoverProps {
  /** Popover header title, e.g. `'Total Human Identities'`. */
  title: string;
  /** Pre-formatted total, e.g. `'1.2M'`. Component never formats. */
  total: string;
  /** Optional category caption row above the item list (e.g. `'Department'`). */
  categoryLabel?: string;
  /** Breakdown items — rendered in array order. */
  items: readonly WidgetBreakdownItem[];
  /** Formatter applied to each item's `count` before display. */
  formatCount: (count: number) => string;
  /** Fixed pixel width of the popover card. Defaults to 180. */
  width?: number;
  /** Extra classes merged onto the root card. */
  className?: string;
}

/**
 * Small card rendered next to the hovered widget number. Layout matches the
 * overview mock: stacked title/total header, horizontal divider, optional
 * category caption, then labeled rows. Mount/unmount is the caller's job —
 * no hover or debounce logic here.
 *
 * Positioning is the caller's job too, and two parts of it are load-bearing:
 *
 *   - The layer. Widget bodies put `relative z-10` on their own content
 *     (AccessDiscoveryBody's columns, MultiSegmentDonut's centre), so a popover
 *     anchored at z-10 ties with them and loses on DOM order to every widget
 *     below it in the grid. Anchor at z-30, the same layer TopicListPopover uses.
 *   - The offset from the anchor is `pl-2` on the wrapper, never `ml-2`. A
 *     margin is dead space outside the hovered subtree: the pointer crossing it
 *     fires the trigger's `onMouseLeave`, which closes the card before it can be
 *     reached — and an unreachable card cannot be scrolled. Padding puts the
 *     same 8px inside the wrapper, so the path from number to list stays hovered.
 */
export const WidgetBreakdownPopover: React.FC<WidgetBreakdownPopoverProps> = ({
  title,
  total,
  categoryLabel,
  items,
  formatCount,
  width = DEFAULT_WIDTH,
  className,
}) => {
  const { listRef, maxHeight, shiftUp } = useListMaxHeight(items.length);
  const listStyle: CSSProperties | undefined =
    maxHeight === undefined ? undefined : { maxHeight: `${maxHeight}px` };

  return (
    <div
      className={cn(
        'flex flex-col gap-4 rounded-[4px] border border-grey-200 bg-dawn-white px-3 py-4 font-outfit text-dawn-black shadow-[0_4px_12px_rgba(0,0,0,0.1)]',
        className
      )}
      style={{
        width: `${width}px`,
        ...(shiftUp > 0 ? { transform: `translateY(-${shiftUp}px)` } : {}),
      }}
    >
      <div className="flex flex-col gap-3">
        <span className="text-[13px] font-medium leading-[1.3]">{title}</span>
        <span className="text-[24px] font-medium leading-[1.2] tabular-nums">{total}</span>
      </div>

      <div className="h-px w-full bg-grey-200" aria-hidden="true" />

      {categoryLabel !== undefined ? (
        <p className="text-[12px] font-normal leading-[1.4] text-grey-400">{categoryLabel}</p>
      ) : null}

      {/*
       * Bounded + scrolled, not sized to content: `items` is tenant data (file
       * types, NHI categories), and an unbounded list renders a card taller
       * than the screen with most of it unreachable. Same treatment as
       * FilterDropdown's discovered option lists.
       */}
      {items.length > 0 ? (
        <ul ref={listRef} style={listStyle} className="flex min-h-0 flex-col gap-3 overflow-y-auto">
          {items.map((item) => {
            const isHighlighted = item.isHighlighted === true;
            return (
              <li
                key={item.id}
                data-testid="widget-breakdown-row"
                className={cn(
                  'flex items-center justify-between gap-2',
                  isHighlighted && 'text-error'
                )}
              >
                <span className="truncate text-[12px] font-normal leading-[1.4] text-[#525252]">
                  {item.label}
                </span>
                <span
                  className={cn(
                    'shrink-0 text-[12px] font-medium leading-[1.4] tabular-nums',
                    isHighlighted ? 'text-error' : 'text-dawn-black'
                  )}
                >
                  {formatCount(item.count)}
                </span>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
};

WidgetBreakdownPopover.displayName = 'WidgetBreakdownPopover';
